import type { AssetData } from '@/types/api';

/**
 * Group assets by their free-form category. Uncategorized assets fall under
 * "Other". Named categories sort alphabetically; "Other" always sorts last.
 */
export function groupAssetsByCategory<T extends { category: string | null }>(
  assets: T[],
): Array<[string, T[]]> {
  const map = new Map<string, T[]>();
  for (const asset of assets) {
    const key = asset.category?.trim() || 'Other';
    const bucket = map.get(key);
    if (bucket) {
      bucket.push(asset);
    } else {
      map.set(key, [asset]);
    }
  }
  return [...map.entries()].sort(([a], [b]) => {
    if (a === 'Other') return 1;
    if (b === 'Other') return -1;
    return a.localeCompare(b);
  });
}

/** Distinct category names present in a set of assets (for autocomplete). */
export function assetCategories(assets: AssetData[]): string[] {
  const set = new Set<string>();
  for (const asset of assets) {
    const c = asset.category?.trim();
    if (c) set.add(c);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}
